"use client"

import { useState, useEffect } from "react"
import { useAuth } from "../context/AuthContext"
import { useParams, useNavigate } from "react-router-dom"
import axios from "axios"
import config from "../config"
import { Helmet } from 'react-helmet-async'

const ITEMS_PER_PAGE = 6

const PublicProfile = () => {
  const eventTitle = process.env.REACT_APP_EVENT_TITLE || "EventSawa"
  const eventDescription = process.env.REACT_APP_EVENT_DESCRIPTION || "We make events sawa"
  const { user: loggedInUser } = useAuth()
  const { slug } = useParams()
  const navigate = useNavigate()

  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [profileGigs, setProfileGigs] = useState([])
  const [profileReviews, setProfileReviews] = useState([])
  const [profileEvents, setProfileEvents] = useState([])
  const [searchQuery, setSearchQuery] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedReview, setSelectedReview] = useState(null)
  const [activeTab, setActiveTab] = useState("portfolio")
  const [profileStats, setProfileStats] = useState({
    totalGigs: 0,
    completedGigs: 0,
    ongoingGigs: 0,
    totalReviews: 0,
    averageRating: 0,
    totalEvents: 0,
    upcomingEvents: 0,
    totalAttendees: 0,
  })
  const [uniqueCategories, setUniqueCategories] = useState([])

  useEffect(() => {
    const token = localStorage.getItem("access_token")
    if (slug) {
      axios
        .get(`${config.API_BASE_URL}/profiles/profile/slug/${slug}/`, {
          headers: { Authorization: token ? `Bearer ${token}` : "" },
        })
        .then((response) => {
          setProfile(response.data)
          setLoading(false)
        })
        .catch(() => {
          setError("Profile not found or unauthorized")
          setLoading(false)
        })
    }
  }, [slug])

  // Load the viewed organizer's PUBLIC events (works for any visitor, not just
  // the profile owner) via the public organizer endpoint.
  useEffect(() => {
    const username = profile?.username || profile?.user?.username
    if (!username) return
    axios
      .get(`${config.API_BASE_URL}/events/organizer/${username}/events/`)
      .then((res) => {
        const evs = res.data?.events || []
        setProfileEvents(evs)
        setUniqueCategories([...new Set(evs.map((e) => e.category).filter(Boolean))])
      })
      .catch(() => {})
  }, [profile])

  useEffect(() => {
    const fetchUserData = async () => {
      if (!loggedInUser?.id) return
      const token = localStorage.getItem("access_token")

      try {
        // Fetch gigs for service providers
        if (loggedInUser.role === "service_provider") {
          const gigsResponse = await axios.get(`${config.API_BASE_URL}/bids/hired/${loggedInUser.id}/`, {
            headers: { Authorization: token ? `Bearer ${token}` : "" },
          })
          const gigs = gigsResponse.data || []
          setProfileGigs(gigs)

          // Process reviews and stats
          const reviews = []
          let totalRating = 0
          let completed = 0
          let ongoing = 0

          await Promise.all(
            gigs.map(async (gig) => {
              if (gig.status === "completed") completed++
              else ongoing++

              let eventThumbnail = ""
              let eventSlug = ""
              const gigTitle = gig.title || "Untitled Gig"

              try {
                const eventsRes = await axios.get(`${config.API_BASE_URL}/events/all/`, {
                  headers: { Authorization: `Bearer ${token}` },
                })
                const matchedEvent = eventsRes.data.find((ev) => ev.id === gig.event_id)
                if (matchedEvent) {
                  eventSlug = matchedEvent.slug
                  const eventRes = await axios.get(`${config.API_BASE_URL}/events/${eventSlug}/`, {
                    headers: { Authorization: `Bearer ${token}` },
                  })
                  eventThumbnail = eventRes.data?.thumbnail_photo || ""
                }
              } catch (err) {
                // console.error("Error fetching event thumbnail:", err.message)
              }

              try {
                const reviewRes = await axios.get(`${config.API_BASE_URL}/bids/reviews/${gig.id}/`, {
                  headers: { Authorization: `Bearer ${token}` },
                })
                const reviewsData = reviewRes.data || []
                reviewsData.forEach((review) => {
                  if (review?.rating) {
                    totalRating += review.rating
                    reviews.push({
                      ...review,
                      gigTitle: gigTitle,
                      bidId: gig.id,
                      status: gig.status,
                      eventPhoto: eventThumbnail,
                      createdAt: review?.created_at,
                    })
                  }
                })
              } catch (error) {
                // console.error("Error fetching review:", error.message)
              }
            }),
          )

          setProfileReviews(reviews)
          setProfileStats((prev) => ({
            ...prev,
            totalGigs: gigs.length,
            completedGigs: completed,
            ongoingGigs: ongoing,
            totalReviews: reviews.length,
            averageRating: reviews.length ? (totalRating / reviews.length).toFixed(1) : 0,
          }))
        }

        // Fetch events for event organizers
        if (loggedInUser.role === "event_organizer") {
          const eventsResponse = await axios.get(`${config.API_BASE_URL}/events/my-events/`, {
            headers: { Authorization: `Bearer ${token}` },
          })
          const events = eventsResponse.data || []

          // Filter events based on visibility and ownership
          const filteredEvents = events.filter(event => {
            if (event.visibility === 'public') return true;
            return event.created_by === loggedInUser.username;
          });

          setProfileEvents(filteredEvents)

          // Extract unique categories from user's events
          const categories = [...new Set(filteredEvents.map(event => event.category).filter(Boolean))]
          setUniqueCategories(categories)

          const upcomingEvents = filteredEvents.filter((event) => event.status === "upcoming").length

          setProfileStats((prev) => ({
            ...prev,
            totalEvents: filteredEvents.length,
            upcomingEvents: upcomingEvents,
          }))
        }

        // Fetch attended events for event attendees
        if (loggedInUser.role === "event_attendee") {
          // This would need an API endpoint for attended events
          // For now, using placeholder data
          setProfileStats((prev) => ({
            ...prev,
            totalEvents: 12, // Placeholder
            upcomingEvents: 3, // Placeholder
          }))
        }
      } catch (error) {
        // console.error("Failed to fetch user data:", error.message)
      }
    }

    fetchUserData()
  }, [loggedInUser])

  const formatCategoryName = (category) => {
    // Convert underscore category names to readable format
    return category
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  const getDisplayCategories = () => {
    // If we have categories from user's events, use them
    if (uniqueCategories.length > 0) {
      return uniqueCategories.map(formatCategoryName)
    }
    // Fallback to default categories if no user events
    return [
      "Corporate",
      "Weddings", 
      "Conferences",
      "Festivals",
      "Workshops",
      "Networking",
      "Product Launch",
      "Charity",
    ]
  }

  const getRoleSpecificStats = () => {
    switch (profile?.role || loggedInUser?.role) {
      case "service_provider":
        return [
          {
            label: "Total Gigs",
            value: profileStats.totalGigs,
            color: "blue",
            icon: "M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6",
            subLines: [`Completed: ${profileStats.completedGigs}`, `Ongoing: ${profileStats.ongoingGigs}`],
          },
          {
            label: "Client Reviews",
            value: profileStats.totalReviews,
            color: "green",
            icon: "M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z",
          },
          {
            label: "Average Rating",
            value: `${profileStats.averageRating} ★`,
            color: "yellow",
            icon: "M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z",
          },
        ]

      case "event_organizer":
        return []

      case "event_attendee":
        return [
          {
            label: "Events Attended",
            value: profileStats.totalEvents,
            color: "green",
            icon: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z",
          },
          {
            label: "Upcoming Events",
            value: profileStats.upcomingEvents,
            color: "blue",
            icon: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z",
          },
          {
            label: "Favorite Categories",
            value: "5",
            color: "purple",
            icon: "M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z",
          },
        ]

      default:
        return []
    }
  }

  const renderPortfolioContent = () => {
    const userRole = profile?.role || loggedInUser?.role

    switch (userRole) {
      case "service_provider":
        return (
          <div className="space-y-8">
            {/* Services Offered */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                <svg className="w-6 h-6 mr-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6"
                  />
                </svg>
                Services Offered
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {["Photography", "Videography", "Event Planning", "Catering", "Sound & Lighting", "Decoration"].map(
                  (service, idx) => (
                    <div
                      key={idx}
                      className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <span className="font-medium text-gray-800">{service}</span>
                      </div>
                    </div>
                  ),
                )}
              </div>
            </div>

            {/* Skills & Expertise */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                <svg className="w-6 h-6 mr-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                  />
                </svg>
                Skills & Expertise
              </h3>
              <div className="flex flex-wrap gap-3">
                {[
                  "Wedding Photography",
                  "Corporate Events",
                  "Birthday Parties",
                  "Product Launch",
                  "Conference Management",
                  "Live Streaming",
                ].map((skill, idx) => (
                  <span
                    key={idx}
                    className="px-4 py-2 bg-white rounded-full text-sm font-medium text-gray-700 shadow-sm border border-gray-200"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )

      case "event_organizer":
        return (
          <div className="space-y-8">
            {/* Recent Events */}
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                <svg className="w-6 h-6 mr-3 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                Recent Events
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {profileEvents
                  .slice((currentPage - 1) * 6, currentPage * 6)
                  .map((event, idx) => (
                    <div
                      key={event.id}
                      className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
                    >
                      <div className="h-48 relative">
                        <img
                          src={`${config.MEDIA_BASE_URL}${event.thumbnail_photo || '/default-event.jpg'}`}
                          alt={event.title}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-20"></div>
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                      </div>
                      <div className="p-4">
                        <h4 className="font-bold text-gray-800 mb-2">{event.title}</h4>
                        <p className="text-sm text-gray-600 mb-2">{new Date(event.date).toLocaleDateString()}</p>
                        <div className="flex items-center">
                          <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-full">
                            {event.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
              <div className="flex justify-center mt-6">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 bg-purple-600 text-white rounded-md mr-2 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-purple-700 transition-colors"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(Math.ceil(profileEvents.length / 6), prev + 1))}
                  disabled={currentPage === Math.ceil(profileEvents.length / 6)}
                  className="px-4 py-2 bg-purple-600 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-purple-700 transition-colors"
                >
                  Next
                </button>
              </div>
            </div>

            {/* Event Categories */}
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                <svg className="w-6 h-6 mr-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                  />
                </svg>
                Event Specializations
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {getDisplayCategories().map((category, idx) => (
                  <div
                    key={idx}
                    className="bg-white rounded-xl p-4 text-center shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                  >
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="font-medium text-gray-800">{category}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )

      case "event_attendee":
        return (
          <div className="space-y-8">
            {/* Interests */}
            <div className="bg-gradient-to-r from-green-50 to-teal-50 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                <svg className="w-6 h-6 mr-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                  />
                </svg>
                Interests & Preferences
              </h3>
              <div className="flex flex-wrap gap-3">
                {[
                  "Technology",
                  "Music",
                  "Food & Dining",
                  "Networking",
                  "Art & Culture",
                  "Sports",
                  "Business",
                  "Education",
                ].map((interest, idx) => (
                  <span
                    key={idx}
                    className="px-4 py-2 bg-white rounded-full text-sm font-medium text-gray-700 shadow-sm border border-gray-200"
                  >
                    {interest}
                  </span>
                ))}
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                <svg className="w-6 h-6 mr-3 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Recent Activity
              </h3>
              <div className="space-y-4">
                {[
                  { event: "Tech Conference 2024", date: "2 days ago", type: "Attended" },
                  { event: "Startup Networking", date: "1 week ago", type: "Registered" },
                  { event: "Food Festival", date: "2 weeks ago", type: "Attended" },
                ].map((activity, idx) => (
                  <div
                    key={idx}
                    className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex items-center justify-between"
                  >
                    <div>
                      <h4 className="font-medium text-gray-800">{activity.event}</h4>
                      <p className="text-sm text-gray-500">{activity.date}</p>
                    </div>
                    <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-medium">
                      {activity.type}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )

      default:
        return (
          <div className="text-center py-12">
            <p className="text-gray-500">Portfolio content will be displayed based on user role.</p>
          </div>
        )
    }
  }

  const filteredReviews = profileReviews.filter((review) =>
    review.gigTitle.toLowerCase().includes(searchQuery.toLowerCase()),
  )

  const paginatedReviews = filteredReviews.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE)

  const totalPages = Math.ceil(filteredReviews.length / ITEMS_PER_PAGE)

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg font-medium">Loading profile...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <p className="text-red-600 text-lg font-medium">{error}</p>
        </div>
      </div>
    )
  }

  const roleStats = getRoleSpecificStats()

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <Helmet>
        <title>{eventTitle}</title>
        <meta name="description" content={eventDescription} />
      </Helmet>

      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-700 text-white">
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <div className="relative inline-block">
              {profile?.profile_picture ? (
                <img
                  src={profile.profile_picture}
                  alt="Profile"
                  className="w-24 h-24 rounded-full border-4 border-white shadow-lg mx-auto mb-4"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-gray-200 border-4 border-white shadow-lg mx-auto mb-4 flex items-center justify-center">
                  <svg 
                    className="w-12 h-12 text-gray-400" 
                    fill="currentColor" 
                    viewBox="0 0 20 20"
                  >
                    <path 
                      fillRule="evenodd" 
                      d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" 
                      clipRule="evenodd" 
                    />
                  </svg>
                </div>
              )}
              <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full border-2 border-white"></div>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold mb-1">
              {profile?.first_name} {profile?.last_name}
            </h1>
            <p className="text-lg text-blue-100 mb-1">@{profile?.slug}</p>
            <div className="inline-flex items-center px-3 py-1 bg-gray-50 rounded-full text-sm font-medium mb-4 hover:bg-gray-100 transition-all duration-200">
              <svg className="w-3.5 h-3.5 mr-1 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
              <span className="text-gray-700">{(profile?.role || loggedInUser?.role)?.replace("_", " ").replace(/\b\w/g, (l) => l.toUpperCase())}</span>
            </div>
            {loggedInUser?.username?.toLowerCase() === profile?.slug?.toLowerCase() && (
              <button
                onClick={() => navigate("/profile/edit")}
                className="inline-flex items-center px-3 py-1 bg-blue-100 rounded-full text-sm font-medium ml-4 hover:bg-blue-200 hover:shadow-sm transition-all duration-200"
              >
                <svg className="w-3.5 h-3.5 mr-1 text-blue-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
                <span className="text-blue-800">Edit Profile</span>
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {roleStats.map((stat, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
            >
              <div className="flex items-center">
                <div className={`h-12 w-12 bg-${stat.color}-100 rounded-xl flex items-center justify-center mr-4`}>
                  <svg
                    className={`h-6 w-6 text-${stat.color}-600`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={stat.icon} />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">{stat.label}</p>
                  <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                  {stat.subLines && (
                    <div className="text-xs text-gray-400 mt-1">
                      {stat.subLines.map((line, idx) => (
                        <div key={idx}>{line}</div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {(profile?.role === "service_provider" ? ["portfolio", "about", "reviews"] : ["portfolio", "about"]).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${activeTab === tab
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-8">
            {activeTab === "portfolio" && renderPortfolioContent()}

            {activeTab === "about" && (
              <div className="space-y-8">
                <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-2xl p-8">
                  <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                    {/* <svg className="w-6 h-6 mr-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg> */}
                    {/* About Me */}
                  </h3>
                  <p className="text-gray-700 text-lg leading-relaxed">
                    {profile?.bio || "No bio available. This user hasn't shared their story yet."}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                    <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                      <svg
                        className="w-5 h-5 mr-2 text-green-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9"
                        />
                      </svg>
                      Website
                    </h4>
                    {profile?.website ? (
                      <a
                        href={profile.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                      >
                        {profile.website}
                      </a>
                    ) : (
                      <span className="text-gray-400 italic">No website provided</span>
                    )}
                  </div>


                </div>
              </div>
            )}

            {activeTab === "reviews" && profileReviews.length > 0 && profile?.role === "service_provider" && (
              <div className="space-y-8">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <h3 className="text-2xl font-bold text-gray-800">
                    {loggedInUser?.username === profile?.slug ? "Your Reviews" : "Client Reviews"}
                  </h3>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search reviews..."
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value)
                        setCurrentPage(1)
                      }}
                      className="w-full sm:w-80 px-4 py-3 pl-12 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    />
                    <svg
                      className="absolute left-4 top-3.5 h-5 w-5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {paginatedReviews.map((review, idx) => (
                    <div
                      key={idx}
                      className="bg-white border border-gray-200 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 overflow-hidden"
                    >
                      <div className="relative h-48 bg-gradient-to-br from-blue-400 to-purple-500">
                        {review.eventPhoto && (
                          <img
                            src={`${config.MEDIA_BASE_URL}${review.eventPhoto}`}
                            alt="Event"
                            className="w-full h-full object-cover"
                          />
                        )}
                        <div className="absolute top-4 right-4 bg-white bg-opacity-90 rounded-full px-3 py-1">
                          <div className="flex items-center">
                            <span className="text-sm font-bold text-gray-800 mr-1">{review.rating}</span>
                            <div className="flex">
                              {[...Array(5)].map((_, i) => (
                                <svg
                                  key={i}
                                  className={`w-4 h-4 ${i < review.rating ? "text-yellow-400" : "text-gray-300"}`}
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="p-6">
                        <h4 className="text-lg font-bold text-gray-800 mb-2">{review.gigTitle}</h4>
                        <p className="text-sm text-gray-500 mb-4">
                          {new Date(review.createdAt).toLocaleDateString("en-US", {
                            month: "long",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </p>
                        <p className="text-gray-700 line-clamp-3">{review.review_text}</p>
                        <button
                          onClick={() => setSelectedReview(review)}
                          className="mt-4 text-blue-600 hover:text-blue-800 font-medium text-sm transition-colors"
                        >
                          Read more →
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className="flex justify-center items-center space-x-4 mt-8">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Previous
                    </button>
                    <span className="text-gray-600">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Review Modal */}
      {selectedReview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-4xl rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="relative">
              <button
                className="absolute top-4 right-4 z-10 w-10 h-10 bg-white bg-opacity-90 rounded-full flex items-center justify-center text-gray-600 hover:text-red-500 transition-colors"
                onClick={() => setSelectedReview(null)}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              <div className="h-64 bg-gradient-to-br from-blue-400 to-purple-500 relative">
                {selectedReview.eventPhoto && (
                  <img
                    src={`${config.MEDIA_BASE_URL}${selectedReview.eventPhoto}`}
                    alt="Event"
                    className="w-full h-full object-cover"
                  />
                )}
              </div>

              <div className="p-8">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-3xl font-bold text-gray-800">{selectedReview.gigTitle}</h2>
                  <div className="flex items-center bg-gray-100 rounded-full px-4 py-2">
                    <span className="text-lg font-bold text-gray-800 mr-2">{selectedReview.rating}</span>
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <svg
                          key={i}
                          className={`w-5 h-5 ${i < selectedReview.rating ? "text-yellow-400" : "text-gray-300"}`}
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                  </div>
                </div>

                <p className="text-gray-500 mb-6">
                  {new Date(selectedReview.createdAt).toLocaleDateString("en-US", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </p>

                <div className="prose max-w-none">
                  <p className="text-gray-700 text-lg leading-relaxed">{selectedReview.review_text}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PublicProfile